import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ExploreCard } from "@/components/ExploreCard";
import { ExploreDesignModal, type SimilarDesign } from "@/components/ExploreDesignModal";
import { ExploreCardSkeletonGrid } from "@/components/ExploreCardSkeleton";
import { Button } from "@/components/ui/button";
import { ArrowRight, Compass, SlidersHorizontal, Home, LogIn } from "lucide-react";
import { isNativePlatform } from "@/lib/platform";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { User } from "@supabase/supabase-js";
import { Seo } from "@/components/Seo";
import { attachPublicUsernames } from "@/lib/public-designers";

const PAGE_SIZE = 20;

interface PublicDesign {
  id: string;
  transformed_image_url: string | null;
  original_image_url: string;
  design_style: string;
  room_type: string;
  custom_prompt: string | null;
  designer_note: string | null;
  created_at: string;
  all_variations: any;
  public_variations: any;
  profiles: { username: string } | null;
}

// Resolve which variations to display on Explore: prefer public_variations
// (the curated subset the user picked); fall back to all_variations for
// legacy rows shared before the public_variations column existed.
const getDisplayVariations = (d: Pick<PublicDesign, "public_variations" | "all_variations">): string[] => {
  if (Array.isArray(d.public_variations) && d.public_variations.length > 0) {
    return d.public_variations as string[];
  }
  if (Array.isArray(d.all_variations) && d.all_variations.length > 0) {
    return d.all_variations as string[];
  }
  return [];
};

// Cover image for the Explore card: first item of the resolved variations,
// falling back to transformed_image_url, then original_image_url.
const getDisplayCover = (
  d: Pick<PublicDesign, "public_variations" | "all_variations" | "transformed_image_url" | "original_image_url">,
): string => {
  const vars = getDisplayVariations(d);
  return vars[0] || d.transformed_image_url || d.original_image_url;
};

const notifyExploreInteraction = async (designId: string, actionType: "like" | "save") => {
  try {
    await supabase.functions.invoke("notify-explore-interaction", {
      body: { design_id: designId, action_type: actionType },
    });
  } catch {
    // Silent fail — notification is best-effort
  }
};

// sessionStorage cache helpers — instant hydration to avoid empty-icon flicker.
const likedKey = (uid: string) => `explore:liked:${uid}`;
const savedKey = (uid: string) => `explore:saved:${uid}`;

const readCache = (key: string): string[] => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCache = (key: string, ids: Set<string>) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {
    // ignore quota errors
  }
};

const Explore = () => {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState<PublicDesign[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [styleFilter, setStyleFilter] = useState<string>("all");
  const [roomFilter, setRoomFilter] = useState<string>("all");
  const [styles, setStyles] = useState<string[]>([]);
  const [rooms, setRooms] = useState<string[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const pendingLikesRef = useRef<Set<string>>(new Set());
  const pendingSavesRef = useRef<Set<string>>(new Set());

  // Auth & social state
  const [user, setUser] = useState<User | null>(null);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});

  // Modal state
  const [selectedDesign, setSelectedDesign] = useState<PublicDesign | null>(null);

  // Refs for mount/auth coordination
  const loadedDesignIdsRef = useRef<string[]>([]);
  const authResolvedRef = useRef(false);
  const [authVersion, setAuthVersion] = useState(0); // bumps when auth resolves to retrigger fetch effect

  // Get auth state — hydrate cache immediately, mark auth resolved to unblock fetch
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      // Hydrate from sessionStorage for instant filled icons
      if (sessionUser) {
        const cachedLiked = readCache(likedKey(sessionUser.id));
        const cachedSaved = readCache(savedKey(sessionUser.id));
        if (cachedLiked.length) setLikedIds(new Set(cachedLiked));
        if (cachedSaved.length) setSavedIds(new Set(cachedSaved));
      }
      authResolvedRef.current = true;
      setAuthVersion((v) => v + 1);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const newUser = session?.user ?? null;
      setUser(newUser);

      if (event === "SIGNED_OUT") {
        // Clear local interaction state and any cached entries we know about.
        setLikedIds(new Set());
        setSavedIds(new Set());
        try {
          // Best-effort: sweep all explore: cache keys.
          const toRemove: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && (k.startsWith("explore:liked:") || k.startsWith("explore:saved:"))) {
              toRemove.push(k);
            }
          }
          toRemove.forEach((k) => sessionStorage.removeItem(k));
        } catch {
          // ignore
        }
        return;
      }

      if (newUser) {
        // Hydrate cache for the freshly-signed-in user
        const cachedLiked = readCache(likedKey(newUser.id));
        const cachedSaved = readCache(savedKey(newUser.id));
        if (cachedLiked.length) setLikedIds(new Set(cachedLiked));
        if (cachedSaved.length) setSavedIds(new Set(cachedSaved));
        // Refresh authoritative state if we already have designs loaded
        if (loadedDesignIdsRef.current.length > 0) {
          fetchAllUserInteractions(newUser.id);
        }
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist likedIds / savedIds to sessionStorage whenever they change
  useEffect(() => {
    if (user) writeCache(likedKey(user.id), likedIds);
  }, [likedIds, user]);

  useEffect(() => {
    if (user) writeCache(savedKey(user.id), savedIds);
  }, [savedIds, user]);

  const fetchAllUserInteractions = async (userId: string) => {
    const ids = loadedDesignIdsRef.current;
    if (ids.length === 0) return;

    const CHUNK = 500;
    const allLikedIds: string[] = [];
    const allSavedIds: string[] = [];

    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      const [{ data: likes }, { data: saves }] = await Promise.all([
        supabase.from("explore_likes").select("design_id").eq("user_id", userId).in("design_id", chunk),
        supabase.from("explore_saves").select("design_id").eq("user_id", userId).in("design_id", chunk),
      ]);
      if (likes) allLikedIds.push(...likes.map((l) => l.design_id));
      if (saves) allSavedIds.push(...saves.map((s) => s.design_id));
    }

    // Authoritative reconciliation: for the loaded slice, the DB result IS the truth.
    // Remove any IDs in the loaded slice from prev, then add back only the DB-confirmed ones.
    // Preserve IDs outside the loaded slice (defensive — e.g. modal-only design).
    const idSet = new Set(ids);
    const dbLiked = new Set(allLikedIds);
    const dbSaved = new Set(allSavedIds);
    setLikedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => { if (!idSet.has(id)) next.add(id); });
      dbLiked.forEach((id) => next.add(id));
      return next;
    });
    setSavedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => { if (!idSet.has(id)) next.add(id); });
      dbSaved.forEach((id) => next.add(id));
      return next;
    });
  };

  const fetchDesigns = useCallback(
    async (offset: number, append: boolean = false) => {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      let query = supabase
        .from("saved_designs")
        .select("id, user_id, transformed_image_url, original_image_url, design_style, room_type, custom_prompt, designer_note, created_at, all_variations, public_variations")
        .eq("is_public", true)
        .order("created_at", { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (styleFilter !== "all") query = query.eq("design_style", styleFilter);
      if (roomFilter !== "all") query = query.eq("room_type", roomFilter);

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching public designs:", error);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const withUsernames = await attachPublicUsernames((data || []) as any[]);
      const results = withUsernames as unknown as PublicDesign[];
      setHasMore(results.length === PAGE_SIZE);

      if (append) {
        setDesigns((prev) => [...prev, ...results]);
      } else {
        setDesigns(results);
      }

      // Track loaded IDs
      const newIds = results.map((d) => d.id);
      if (append) {
        loadedDesignIdsRef.current = [...loadedDesignIdsRef.current, ...newIds];
      } else {
        loadedDesignIdsRef.current = newIds;
      }

      // Fetch like counts for these designs
      if (newIds.length > 0) {
        fetchLikeCounts(newIds);
        if (user) fetchUserInteractions(newIds);
      }

      setLoading(false);
      setLoadingMore(false);
    },
    [styleFilter, roomFilter, user]
  );

  const fetchLikeCounts = async (designIds: string[]) => {
    const { data } = await supabase.rpc("explore_like_counts", { design_ids: designIds });

    if (data) {
      const counts: Record<string, number> = {};
      data.forEach((l) => {
        counts[l.design_id] = Number(l.like_count) || 0;
      });
      setLikeCounts((prev) => ({ ...prev, ...counts }));
    }
  };

  const fetchUserInteractions = async (designIds: string[]) => {
    if (!user) return;
    const [{ data: likes }, { data: saves }] = await Promise.all([
      supabase.from("explore_likes").select("design_id").eq("user_id", user.id).in("design_id", designIds),
      supabase.from("explore_saves").select("design_id").eq("user_id", user.id).in("design_id", designIds),
    ]);
    // Authoritative for the queried batch: drop any batch IDs from prev, then add DB-confirmed back.
    const batchSet = new Set(designIds);
    const dbLiked = new Set((likes || []).map((l) => l.design_id));
    const dbSaved = new Set((saves || []).map((s) => s.design_id));
    setLikedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => { if (!batchSet.has(id)) next.add(id); });
      dbLiked.forEach((id) => next.add(id));
      return next;
    });
    setSavedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => { if (!batchSet.has(id)) next.add(id); });
      dbSaved.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleLike = async (designId: string) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    // In-flight guard — ignore rapid second clicks on the same design.
    if (pendingLikesRef.current.has(designId)) return;
    pendingLikesRef.current.add(designId);

    const isLiked = likedIds.has(designId);
    const prevCount = likeCounts[designId] || 0;

    // Optimistic update
    if (isLiked) {
      setLikedIds((prev) => { const n = new Set(prev); n.delete(designId); return n; });
      setLikeCounts((prev) => ({ ...prev, [designId]: Math.max(0, prevCount - 1) }));
    } else {
      setLikedIds((prev) => new Set([...prev, designId]));
      setLikeCounts((prev) => ({ ...prev, [designId]: prevCount + 1 }));
    }

    try {
      if (isLiked) {
        const { error } = await supabase.from("explore_likes").delete().eq("user_id", user.id).eq("design_id", designId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("explore_likes").insert({
          user_id: user.id,
          design_id: designId,
        });
        // 23505 = unique violation (already liked) — treat as success
        if (error && error.code !== "23505") throw error;
        if (!error) notifyExploreInteraction(designId, "like");
      }
    } catch {
      // Rollback on failure — state AND cache.
      if (isLiked) {
        setLikedIds((prev) => {
          const n = new Set([...prev, designId]);
          if (user) writeCache(likedKey(user.id), n);
          return n;
        });
        setLikeCounts((prev) => ({ ...prev, [designId]: prevCount }));
      } else {
        setLikedIds((prev) => {
          const n = new Set(prev);
          n.delete(designId);
          if (user) writeCache(likedKey(user.id), n);
          return n;
        });
        setLikeCounts((prev) => ({ ...prev, [designId]: prevCount }));
      }
    } finally {
      pendingLikesRef.current.delete(designId);
    }
  };

  const handleSave = async (designId: string) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (pendingSavesRef.current.has(designId)) return;
    pendingSavesRef.current.add(designId);

    const isSaved = savedIds.has(designId);

    // Optimistic update
    if (isSaved) {
      setSavedIds((prev) => { const n = new Set(prev); n.delete(designId); return n; });
    } else {
      setSavedIds((prev) => new Set([...prev, designId]));
    }

    try {
      if (isSaved) {
        const { error } = await supabase.from("explore_saves").delete().eq("user_id", user.id).eq("design_id", designId);
        if (error) throw error;
        toast({ title: "Removed", description: "Design removed from your saves" });
      } else {
        const { error } = await supabase.from("explore_saves").insert({
          user_id: user.id,
          design_id: designId,
        });
        // 23505 = unique violation (already saved) — treat as success
        if (error && error.code !== "23505") throw error;
        if (!error) notifyExploreInteraction(designId, "save");
        toast({ title: "Saved!", description: "Design saved to your collection" });
      }
    } catch {
      // Rollback on failure — state AND cache.
      if (isSaved) {
        setSavedIds((prev) => {
          const n = new Set([...prev, designId]);
          if (user) writeCache(savedKey(user.id), n);
          return n;
        });
      } else {
        setSavedIds((prev) => {
          const n = new Set(prev);
          n.delete(designId);
          if (user) writeCache(savedKey(user.id), n);
          return n;
        });
      }
      toast({ title: "Error", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      pendingSavesRef.current.delete(designId);
    }
  };

  // Load filter options once
  useEffect(() => {
    const loadFilters = async () => {
      const [{ data: styleData }, { data: roomData }] = await Promise.all([
        supabase.from("saved_designs").select("design_style").eq("is_public", true),
        supabase.from("saved_designs").select("room_type").eq("is_public", true),
      ]);
      if (styleData) setStyles([...new Set(styleData.map((d) => d.design_style))].sort());
      if (roomData) setRooms([...new Set(roomData.map((d) => d.room_type))].sort());
    };
    loadFilters();
  }, []);

  // Initial load & filter change — gated on auth being resolved so user-scoped
  // interactions are fetched in the same pass as the designs (no race).
  useEffect(() => {
    if (!authResolvedRef.current) return;
    setDesigns([]);
    fetchDesigns(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleFilter, roomFilter, authVersion, user?.id]);

  // Auto-open modal from ?design= query param (for shared links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sharedId = params.get("design");
    if (!sharedId || selectedDesign) return;

    const loadShared = async () => {
      const { data } = await supabase
        .from("saved_designs")
        .select("id, user_id, transformed_image_url, original_image_url, design_style, room_type, custom_prompt, designer_note, created_at, all_variations, public_variations")
        .eq("id", sharedId)
        .eq("is_public", true)
        .maybeSingle();

      if (data) {
        const [withUsername] = await attachPublicUsernames([data as any]);
        setSelectedDesign(withUsername as unknown as PublicDesign);
        fetchLikeCounts([data.id]);
        if (user) fetchUserInteractions([data.id]);
      } else {
        // Design no longer available (deleted or made private). Strip the param
        // so the user lands on a clean Explore feed instead of a stuck/empty state.
        toast({
          title: "Design unavailable",
          description: "This design is no longer public or has been removed.",
        });
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("design");
          window.history.replaceState({}, "", url.toString());
        } catch {
          // ignore
        }
      }
    };
    loadShared();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Refresh interactions when the tab becomes visible / window regains focus.
  useEffect(() => {
    const refresh = () => {
      if (!user) return;
      if (document.visibilityState !== "visible") return;
      if (loadedDesignIdsRef.current.length === 0) return;
      fetchAllUserInteractions(user.id);
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Infinite scroll
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchDesigns(designs.length, true);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, designs.length, fetchDesigns]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <Seo
        title="Explore AI Room Designs — Bedrooms, Living Rooms, Kitchens & More | Decor Canvas"
        description="Browse a community gallery of AI-generated interior designs across 90+ styles and 23 room types. Get inspiration for your next bedroom, living room, kitchen, or pooja room makeover."
        canonicalPath="/explore"
      />
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10 safe-area-inset-top">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {!isNativePlatform() ? (
              <div className="flex items-center gap-2">
                <Button variant="default" size="sm" className="gap-1.5 font-semibold" onClick={() => navigate("/")}>
                  <Home className="h-4 w-4" />
                  <span>Home</span>
                </Button>
                {!user && (
                  <Button variant="outline" size="sm" className="gap-1.5 font-semibold" onClick={() => navigate("/auth")}>
                    <LogIn className="h-4 w-4" />
                    <span className="hidden sm:inline">Sign In</span>
                  </Button>
                )}
              </div>
            ) : (
              <div />
            )}
            <Link
              to={user ? "/designer" : "/auth?redirect=/designer"}
              aria-label={user ? "Design your own room" : "Sign in to start designing"}
              className="group inline-flex items-center gap-2 text-xl sm:text-2xl font-bold text-foreground hover:text-primary transition-colors"
            >
              <h1 className="text-xl sm:text-2xl font-bold">
                {user ? "Design Your Own Room" : "Start Designing"}
              </h1>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-foreground">
            Discover inspiring room transformations shared by our community.
          </h2>
          {!isNativePlatform() && (
            <div className="flex items-center gap-3 shrink-0">
              <a
                href="https://play.google.com/store/apps/details?id=com.atheniaai.decorcanvas"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
                aria-label="Get it on Google Play"
              >
                <svg viewBox="0 0 135 40" className="h-10 w-auto" fill="none">
                  <rect width="135" height="40" rx="8" fill="#000" />
                  <path d="M20 10.5l10 9.5-10 9.5V10.5z" fill="#34A853" />
                  <path d="M20 10.5l12.5 7-2.5 2.5L20 10.5z" fill="#FBBC04" />
                  <path d="M20 29.5l10-9.5 2.5 2.5L20 29.5z" fill="#EA4335" />
                  <path d="M32.5 17.5L35 20l-2.5 2.5-2.5-2.5 2.5-2.5z" fill="#4285F4" />
                  <text x="44" y="17" fill="white" fontSize="8" fontFamily="system-ui, sans-serif">
                    GET IT ON
                  </text>
                  <text x="44" y="28" fill="white" fontSize="12" fontWeight="bold" fontFamily="system-ui, sans-serif">
                    Google Play
                  </text>
                </svg>
              </a>
              <a
                href="https://apps.apple.com/app/id6758079242"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
                aria-label="Download on the App Store"
              >
                <svg viewBox="0 0 135 40" className="h-10 w-auto" fill="none">
                  <rect width="135" height="40" rx="8" fill="#000" />
                  <path
                    d="M24 12c1.1-1.5 3-2.5 4.5-2.5.2 1.5-.4 3-1.5 4.1-1 1.1-2.7 2-4.3 1.9-.2-1.4.5-2.9 1.3-3.5zm5.8 5.5c-1.3.7-2.2 2.1-2.2 3.5 0 2.8 2 4.5 2 4.5s-1.1 3.2-2.7 3.2c-.7 0-1.3-.5-2.1-.5-.8 0-1.5.5-2.1.5-1.5 0-3.5-3.1-3.5-5.6 0-3.3 2.1-5 4.1-5 1 0 1.8.6 2.4.6.6 0 1.5-.7 2.6-.6.5 0 1.1.1 1.5.4z"
                    fill="white"
                  />
                  <text x="40" y="17" fill="white" fontSize="8" fontFamily="system-ui, sans-serif">
                    Download on the
                  </text>
                  <text x="40" y="28" fill="white" fontSize="12" fontWeight="bold" fontFamily="system-ui, sans-serif">
                    App Store
                  </text>
                </svg>
              </a>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
          <Select value={styleFilter} onValueChange={setStyleFilter}>
            <SelectTrigger className="w-[160px] h-9 text-sm">
              <SelectValue placeholder="All Styles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Styles</SelectItem>
              {styles.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={roomFilter} onValueChange={setRoomFilter}>
            <SelectTrigger className="w-[160px] h-9 text-sm">
              <SelectValue placeholder="All Rooms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Rooms</SelectItem>
              {rooms.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-4">
            <ExploreCardSkeletonGrid count={12} />
          </div>
        ) : designs.length === 0 ? (
          <div className="text-center py-20">
            <Compass className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No designs to explore yet</h3>
            <p className="text-muted-foreground mb-4">Be the first to share a design from your projects!</p>
            <Button onClick={() => navigate("/designer")}>Start Designing</Button>
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-4">
            {designs.map((design, index) => (
              <ExploreCard
                key={design.id}
                designId={design.id}
                imageUrl={getDisplayCover(design)}
                variations={getDisplayVariations(design)}
                designStyle={design.design_style}
                roomType={design.room_type}
                username={design.profiles?.username || "anonymous"}
                customPrompt={design.custom_prompt}
                designerNote={design.designer_note}
                likeCount={likeCounts[design.id] || 0}
                isLiked={likedIds.has(design.id)}
                isSaved={savedIds.has(design.id)}
                eager={index < 6}
                onLike={handleLike}
                onSave={handleSave}
                onClick={() => setSelectedDesign(design)}
              />
            ))}
          </div>
        )}

        {/* Load more sentinel + skeletons */}
        {loadingMore && (
          <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-4 mt-4">
            <ExploreCardSkeletonGrid count={4} />
          </div>
        )}
        <div ref={sentinelRef} className="h-4" />

        {/* Manual load-more fallback (mobile WebViews can fail to fire IntersectionObserver) */}
        {!loading && !loadingMore && hasMore && designs.length > 0 && (
          <div className="flex justify-center py-6">
            <Button
              variant="outline"
              onClick={() => fetchDesigns(designs.length, true)}
            >
              Load more designs
            </Button>
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      <ExploreDesignModal
        open={!!selectedDesign}
        onClose={() => setSelectedDesign(null)}
        design={
          selectedDesign
            ? {
                id: selectedDesign.id,
                imageUrl: getDisplayCover(selectedDesign),
                designStyle: selectedDesign.design_style,
                roomType: selectedDesign.room_type,
                username: selectedDesign.profiles?.username || "anonymous",
                customPrompt: selectedDesign.custom_prompt,
                designerNote: selectedDesign.designer_note,
                createdAt: selectedDesign.created_at,
                allVariations: getDisplayVariations(selectedDesign).length > 0
                  ? getDisplayVariations(selectedDesign)
                  : undefined,
              }
            : null
        }
        likeCount={selectedDesign ? likeCounts[selectedDesign.id] || 0 : 0}
        isLiked={selectedDesign ? likedIds.has(selectedDesign.id) : false}
        isSaved={selectedDesign ? savedIds.has(selectedDesign.id) : false}
        onLike={handleLike}
        onSave={handleSave}
        onDesignClick={async (sd: SimilarDesign) => {
          // Fetch full record so created_at, custom_prompt and variations are
          // present — otherwise the modal renders "Invalid Date" and loses
          // variation context.
          const { data: full } = await supabase
            .from("saved_designs")
            .select("id, user_id, transformed_image_url, original_image_url, design_style, room_type, custom_prompt, designer_note, created_at, all_variations, public_variations")
            .eq("id", sd.id)
            .eq("is_public", true)
            .maybeSingle();

          const fullWithUsername = full
            ? (await attachPublicUsernames([full as any]))[0]
            : null;

          const asPublic: PublicDesign = fullWithUsername
            ? (fullWithUsername as unknown as PublicDesign)
            : {
                id: sd.id,
                transformed_image_url: sd.transformed_image_url,
                original_image_url: sd.original_image_url,
                design_style: sd.design_style,
                room_type: sd.room_type,
                custom_prompt: null,
                designer_note: sd.designer_note,
                created_at: "",
                all_variations: null,
                public_variations: null,
                profiles: sd.profiles,
              };
          setSelectedDesign(asPublic);
          fetchLikeCounts([sd.id]);
          if (user) fetchUserInteractions([sd.id]);
        }}
        likedIds={likedIds}
        savedIds={savedIds}
        likeCounts={likeCounts}
      />
    </div>
  );
};

export default Explore;
